import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import type { EmploymentForm } from "@/lib/employment/schema";
import type {
  EmploymentList,
  EmploymentOptions,
  EmploymentRequestDetail,
} from "@/lib/employment/types";

export class EmploymentHttpError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "EmploymentHttpError";
  }
}

export const employmentAccessFailed = (error: Error | null) =>
  error instanceof EmploymentHttpError && [401, 403, 404].includes(error.status);

export const employmentImageUrl = (requestId: string, fileId: number) =>
  `/api/embauche/${encodeURIComponent(requestId)}/images/${fileId}`;

export async function employmentRequest<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);

  if (!response.ok) {
    const parsed = z
      .object({ message: z.string() })
      .safeParse(await response.json().catch(() => null));

    throw new EmploymentHttpError(
      parsed.success ? parsed.data.message : "Opération impossible. Réessayez.",
      response.status,
    );
  }

  // SAFETY: these types describe our authenticated API's explicit JSON projections.
  return response.json() as Promise<T>;
}

type EmploymentWrite =
  | { id: string; revision: number; step: number; form: EmploymentForm }
  | { revision: number }
  | { task: "employment:sync" }
  | Record<string, never>;

const json = (method: "POST", body: EmploymentWrite): RequestInit => ({
  method,
  headers: { "content-type": "application/json" },
  body: JSON.stringify(body),
});

export const employmentOptionsQueryOptions = queryOptions({
  queryKey: ["employment", "options"],
  queryFn: () => employmentRequest<EmploymentOptions>("/api/embauche/options"),
  staleTime: 5 * 60_000,
});

export interface EmploymentListQuery {
  sort?: "created" | "employee" | "start" | "status";
  order?: "asc" | "desc";
  status?: "all" | "new" | "progress" | "closed";
  search?: string;
  view?: "submitted" | "drafts" | "all";
  page?: number;
  pageSize?: number;
}

export function employmentListQueryOptions(query: EmploymentListQuery = {}, admin = false) {
  query = {
    view: "submitted",
    search: "",
    page: 1,
    pageSize: 25,
    sort: "created",
    order: "desc",
    status: "all",
    ...query,
  };
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query))
    if (value !== undefined && value !== "") params.set(key, String(value));

  return queryOptions({
    // Admin and user results never share a cache entry, including when a role changes.
    queryKey: ["employment", admin ? "admin-list" : "my-list", query],
    queryFn: () =>
      employmentRequest<EmploymentList>(
        `${admin ? "/api/admin/embauche" : "/api/embauche/"}?${params}`,
      ),
    refetchInterval: 60_000,
    retry: false,
  });
}

export function employmentDetailQueryOptions(id: string, audience: "admin" | "user") {
  return queryOptions({
    queryKey: ["employment", "detail", audience, id],
    queryFn: () =>
      employmentRequest<EmploymentRequestDetail>(`/api/embauche/${encodeURIComponent(id)}/`),
    refetchInterval: 60_000,
    retry: false,
  });
}

export function saveEmploymentDraft(input: {
  id: string;
  revision: number;
  step: number;
  form: EmploymentForm;
}) {
  return employmentRequest<EmploymentRequestDetail>("/api/embauche/drafts", json("POST", input));
}

export function submitEmploymentRequest(id: string, revision: number) {
  return employmentRequest<EmploymentRequestDetail>(
    `/api/embauche/${encodeURIComponent(id)}/submit`,
    json("POST", { revision }),
  );
}

export function refreshEmploymentTicket(id: string) {
  return employmentRequest<EmploymentRequestDetail>(
    `/api/embauche/${encodeURIComponent(id)}/sync`,
    json("POST", {}),
  );
}

export function deleteEmploymentDraft(id: string) {
  return employmentRequest<{ success: true }>(`/api/embauche/${encodeURIComponent(id)}/`, {
    method: "DELETE",
  });
}

export function syncAllEmploymentTickets() {
  return employmentRequest<{ success: true; runId: string }>(
    "/api/admin/tasks",
    json("POST", { task: "employment:sync" }),
  );
}
